import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import type { ChartRequest } from '../../aiChatTypes';
import { safeParse } from '../../aiChatUtils';
import { aiQueryCacheSelectors } from '../../aiQueryCacheSlice';
import type { CachedQueryEntry, ChartFilter } from '../aiQueryTypes';
import { returnToolResponse } from '../llmToolsFramework';
import type { ToolMessage } from '../llmToolsTypes';
import { areAllValuesZero, isRecord, MIN_CHART_DATA_POINTS } from '../utils';

type ChartRow = { label: string; value: number };

// Multi-row format used by generate_report for summary reports (net-worth, savings, etc.)
type SummaryReportRow = { name: string; periods: Array<{ period: string; amount: unknown }> };
type BreakdownRow = {
  category?: string;
  name?: string;
  amount?: number;
  amountAbs?: number;
  type?: 'spending' | 'reimbursement';
};

function applyFilters(entry: CachedQueryEntry, filters: ChartFilter): ChartRow[] {
  if (entry.dataShape === 'breakdown') {
    // fullData may use either `category` (queryResolver/spending) or `name` (generate_report)
    if (!Array.isArray(entry.fullData)) return [];
    let rows = entry.fullData as BreakdownRow[];
    if (filters.categories?.length) {
      rows = rows.filter((r) => filters.categories!.includes(r.category ?? r.name ?? ''));
    }
    if (filters.topN != null) {
      rows = rows.slice(0, filters.topN);
    }
    return rows
      .map((r) => ({
        label: r.category ?? r.name ?? '',
        value: typeof r.amount === 'number' ? r.amount : (r.amountAbs ?? 0),
      }))
      .filter((r) => !!r.label);
  }

  if (entry.dataShape === 'trend') {
    const rawData = entry.fullData as Array<Record<string, unknown>>;

    // Summary-report format: Array<{ name, periods: [{period, amount}] }>
    // Used by generate_report for net-worth, net-income, savings, etc.
    if (rawData.length > 0 && Array.isArray(rawData[0].periods)) {
      const rows = rawData as SummaryReportRow[];
      // Prefer a row named "Net Worth"; fall back to first row
      const primaryRow = rows.find((r) => /net.?worth/i.test(r.name)) ?? rows[0];
      return primaryRow.periods.map((p) => ({
        label: p.period,
        value: typeof p.amount === 'number' ? p.amount : parseFloat(String(p.amount)) || 0,
      }));
    }

    // Flat spending-trend format: Array<{ period, totalSpend }>
    const periods = rawData as Array<{ period: string; totalSpend: number }>;
    return periods.map((p) => ({ label: p.period, value: p.totalSpend }));
  }

  if (entry.dataShape === 'comparison') {
    if (!isRecord(entry.fullData)) return [];
    return Object.entries(entry.fullData as Record<string, unknown>).map(([label, v]) => ({
      label,
      value: isRecord(v) && typeof v.value === 'number' ? v.value : 0,
    }));
  }

  return [];
}

const CHART_TYPES = ['pie', 'bar', 'column', 'line', 'area', 'sidewaysBar'] as const;
type ChartType = (typeof CHART_TYPES)[number];

export const chartTools = {
  render_chart: {
    definition: {
      name: 'render_chart',
      description:
        'Render a chart from a prior data tool result when the result is chartable. ' +
        'Use this tool for visual explanations of trends, comparisons, rankings, breakdowns, proportions, outliers, changes over time, or numeric relationships. ' +
        'Prefer this tool when a visual would make the answer easier to understand than prose alone. ' +
        'Also use this tool when the user asks for a chart, graph, visualization, trend, comparison, breakdown, spending by category, month-over-month change, top merchants, biggest movers, or similar visualizable analysis. ' +
        'Do not use this tool for single-value answers, purely textual explanations, non-numeric results, or data that is not meaningfully chartable. ' +
        'For multi-row reports such as income-expense or balance-sheet, only one data series can be rendered at a time — use the filters.categories field to specify which row (e.g. "Income" or "Expenses") to visualize, or omit it to show the first row. ' +
        'This tool requires a resultId from a prior data tool call such as get_category_data, compare_periods, generate_report, get_investment_holdings, or another result that exposes chartable data. ' +
        'Use the most recent relevant resultId unless the user clearly refers to an earlier result. ' +
        'Use availableRenderHints from that result to choose chartType and field mappings. ' +
        'If a visual answer is appropriate but no valid resultId exists yet, call the appropriate data tool first, then call this chart-rendering tool.',
      parameters: {
        type: 'object' as const,
        properties: {
          resultId: {
            type: 'string' as const,
            description: 'The resultId returned by the data query tool.',
          },
          chartType: {
            type: 'string' as const,
            enum: [...CHART_TYPES],
            description: 'Chart type. Match against availableRenderHints from the query result.',
          },
          title: {
            type: 'string' as const,
            description:
              'Short label shown above the chart (e.g. "Spending by Category — January 2026"). Keep it concise.',
          },
          filters: {
            type: 'object' as const,
            description: 'Optional filters to apply before rendering.',
            properties: {
              topN: {
                type: 'number' as const,
                description: 'Show only the top N items by value.',
              },
              categories: {
                type: 'array' as const,
                items: { type: 'string' as const },
                description: 'Limit chart to these category names only.',
              },
            },
          },
        },
        required: ['resultId', 'chartType'],
      },
    },

    handler: (msg: ToolMessage) => {
      const args = safeParse(msg.arguments) || {};
      const state = store?.getState() as RootState | undefined;

      if (!state) {
        return returnToolResponse(msg, { success: false, error: 'Store not available.' });
      }

      if (!args.resultId || typeof args.resultId !== 'string') {
        return returnToolResponse(msg, { success: false, error: 'resultId is required.' });
      }

      const entry = aiQueryCacheSelectors.getEntry(state, args.resultId);
      if (!entry) {
        return returnToolResponse(msg, {
          success: false,
          error: `No cached result found for resultId "${args.resultId}". Re-run the data query first.`,
        });
      }

      const chartType: ChartType = CHART_TYPES.includes(args.chartType) ? args.chartType : 'bar';
      const filters: ChartFilter = args.filters ?? {};
      const title: string | undefined = typeof args.title === 'string' && args.title ? args.title : undefined;
      const data = applyFilters(entry, filters);

      if (data.length < MIN_CHART_DATA_POINTS || areAllValuesZero(data.map((r) => r.value))) {
        return returnToolResponse(msg, {
          success: false,
          error:
            'Insufficient data to render a chart — fewer than 2 non-zero data points. Summarize the data in text instead.',
        });
      }

      return returnToolResponse(msg, {
        success: true,
        resultId: args.resultId,
        chartType,
        dataShape: entry.dataShape,
        ...(title && { title }),
        data,
        rowCount: data.length,
      });
    },

    chartTransformer: (output: unknown): ChartRequest | null => {
      if (!isRecord(output) || !output.success) return null;
      if (!Array.isArray(output.data) || output.data.length < MIN_CHART_DATA_POINTS) return null;
      const rows = output.data as ChartRow[];
      if (areAllValuesZero(rows.map((r) => r.value))) return null;
      const chartType: ChartType = CHART_TYPES.includes(output.chartType as ChartType)
        ? (output.chartType as ChartType)
        : 'bar';
      return {
        chartType,
        title: typeof output.title === 'string' ? output.title : undefined,
        categoryField: 'label',
        valueFields: ['value'],
        data: rows.map((r) => ({ label: r.label, value: r.value })),
      };
    },
  },
};
